import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Column, Expander } from '@amzn/meridian';

import { getCycleDisplayName } from '../../utils/helpers';
import ApplyAllInput from './shared/ApplyAllInput';
import ConfigPanel from './shared/ConfigPanel';

import './SupportHeadcount.css';

const SupportHeadcount = ({ availableCycles, intervals, supportHeadcounts, overrides, onChange, joined = false }) => {
  const sortedCycles = [...availableCycles].sort();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(sortedCycles[0] || 'CYCLE_1');
  const [editingCell, setEditingCell] = useState(null);

  // Snapshot initial overrides on first load — revert target.
  const initialOverridesRef = useRef(null);
  useEffect(() => {
    if (initialOverridesRef.current === null && overrides) {
      initialOverridesRef.current = JSON.parse(JSON.stringify(overrides));
    }
  }, [overrides]);

  useEffect(() => {
    const sorted = [...availableCycles].sort();
    if (sorted.length > 0) setSelectedCycle((prev) => (sorted.includes(prev) ? prev : sorted[0]));
  }, [availableCycles]);

  useEffect(() => {
    setEditingCell(null);
  }, [selectedCycle]);

  const cycleProcesses = useMemo(() => supportHeadcounts?.[selectedCycle] || [], [supportHeadcounts, selectedCycle]);
  const cycleOverrides = useMemo(() => overrides?.[selectedCycle] || {}, [overrides, selectedCycle]);

  const getSlots = (name) => {
    if (cycleOverrides[name]) return cycleOverrides[name];
    const proc = cycleProcesses.find((p) => p.name === name);
    return proc?.headcounts || Array(intervals.length).fill(0);
  };

  const getOriginalSlots = (name) => {
    const orig = initialOverridesRef.current?.[selectedCycle]?.[name];
    if (orig) return orig;
    const proc = cycleProcesses.find((p) => p.name === name);
    return proc?.headcounts || Array(intervals.length).fill(0);
  };

  const isCellChanged = (name, slotIndex) => {
    const current = getSlots(name);
    const original = getOriginalSlots(name);
    return parseFloat(current[slotIndex] ?? 0) !== parseFloat(original[slotIndex] ?? 0);
  };

  const isRowChanged = (name) => {
    const current = getSlots(name);
    const original = getOriginalSlots(name);
    return current.some((v, i) => parseFloat(v ?? 0) !== parseFloat(original[i] ?? 0));
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const parsed = editingCell.value === '' ? 0 : parseFloat(editingCell.value);
    if (isNaN(parsed)) {
      setEditingCell(null);
      return;
    }
    const current = [...getSlots(editingCell.process)];
    current[editingCell.slot] = parsed;
    onChange(selectedCycle, editingCell.process, current);
    setEditingCell(null);
  };

  const revertCell = (processName, slotIndex) => {
    const original = getOriginalSlots(processName);
    const current = [...getSlots(processName)];
    current[slotIndex] = original[slotIndex] ?? 0;
    onChange(selectedCycle, processName, current);
  };

  const revertRow = (processName) => {
    const original = getOriginalSlots(processName);
    onChange(selectedCycle, processName, [...original]);
  };

  if (!cycleProcesses.length && !Object.keys(supportHeadcounts || {}).length) return null;

  return (
    <ConfigPanel joined={joined} className="sh-config">
      <Column spacing="300" alignmentHorizontal="stretch" width="100%">
        <Expander open={isExpanded} onChange={setIsExpanded} title="Support Headcount" type="inline" spacingInset="300">
          <div className="sh-toolbar">
            <div className="fp-config-tabs">
              {sortedCycles.map((cycle) => (
                <button
                  key={cycle}
                  type="button"
                  className="fp-config-tab"
                  aria-pressed={selectedCycle === cycle}
                  onClick={() => setSelectedCycle(cycle)}>
                  {getCycleDisplayName(cycle)}
                </button>
              ))}
            </div>
          </div>

          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table className="sh-table">
              <thead>
                <tr>
                  <th className="sh-th sh-th-process">Process</th>
                  {intervals.map((interval, idx) => (
                    <th key={idx} className="sh-th sh-th-interval">
                      <span className="sh-slot-line">
                        <span className="sh-slot-primary">{interval.name}</span>
                        <span className="sh-slot-secondary">
                          {interval.startLabel}–{interval.endLabel}
                        </span>
                      </span>
                    </th>
                  ))}
                  <th className="sh-th sh-th-apply">Apply All</th>
                </tr>
              </thead>
              <tbody>
                {cycleProcesses.map((process) => {
                  const slots = getSlots(process.name);
                  const rowChanged = isRowChanged(process.name);
                  return (
                    <tr key={process.name} className="sh-row">
                      <td className="sh-cell sh-cell-process">{process.name}</td>
                      {intervals.map((_, slotIndex) => {
                        const isEditing = editingCell?.process === process.name && editingCell?.slot === slotIndex;
                        const displayValue = slots[slotIndex] || 0;
                        const cellChanged = isCellChanged(process.name, slotIndex);
                        return (
                          <td key={slotIndex} className="sh-cell sh-cell-interval">
                            <div className="sh-cell-wrap">
                              {isEditing ? (
                                <span className="fp-input sh-input">
                                  <input
                                    type="text"
                                    value={editingCell.value}
                                    autoFocus
                                    onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                    onBlur={(e) => {
                                      const parsed = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                      if (isNaN(parsed)) {
                                        setEditingCell(null);
                                        return;
                                      }
                                      const current = [...getSlots(editingCell.process)];
                                      current[editingCell.slot] = parsed;
                                      onChange(selectedCycle, editingCell.process, current);
                                      setEditingCell(null);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') commitEdit();
                                    }}
                                  />
                                </span>
                              ) : (
                                <span
                                  className="sh-value"
                                  onClick={() =>
                                    setEditingCell({
                                      process: process.name,
                                      slot: slotIndex,
                                      value: String(displayValue)
                                    })
                                  }
                                  title="Click to edit">
                                  {parseFloat(displayValue).toFixed(1)}
                                </span>
                              )}
                              {cellChanged && (
                                <button
                                  type="button"
                                  className="sh-revert-cell"
                                  onClick={() => revertCell(process.name, slotIndex)}
                                  title="Revert to original">
                                  ↩
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td className="sh-cell sh-cell-apply">
                        <div className="sh-apply-wrap">
                          <ApplyAllInput
                            min={0}
                            defaultValue="0"
                            inputClassName="fp-input sh-input"
                            btnClassName="sh-apply-btn"
                            onApply={(value) => {
                              const newSlots = Array(intervals.length).fill(value);
                              onChange(selectedCycle, process.name, newSlots);
                            }}
                          />
                        </div>
                        {rowChanged && (
                          <button
                            type="button"
                            className="sh-revert-row"
                            onClick={() => revertRow(process.name)}
                            title="Revert row to original">
                            ↩ Revert
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Expander>
      </Column>
    </ConfigPanel>
  );
};

export default SupportHeadcount;
